import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import { Chip } from '@/components/original/games/baccarat/components/Chip'
import { ROULETTE_CHIP_REAL_VALUES } from '@/config/original'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { useGetCurrenciesQuery } from '@/store/api/paymentApi'
import { useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectRouletteGame } from '@/store/slices/rouletteSlice'
import type { GameController } from '@/components/original/games/roulette/core/GameController'

interface RouletteBetPanelProps {
	controller: GameController | null
	className?: string
}

const CHIP_SCROLL_STEP_PX = 110

export function RouletteBetPanel({ controller, className }: RouletteBetPanelProps) {
	const roulette = useAppSelector(selectRouletteGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: currenciesResponse } = useGetCurrenciesQuery()
	const scrollRef = useRef<HTMLDivElement>(null)
	const chipButtonRefs = useRef<Array<HTMLButtonElement | null>>([])
	const [canScrollPrev, setCanScrollPrev] = useState(false)
	const [canScrollNext, setCanScrollNext] = useState(false)

	const currencies = currenciesResponse?.data ?? []
	const currency = useMemo(() => currencies.find((item) => item.currency_code.toUpperCase() === roulette.currencyCode.toUpperCase()), [currencies, roulette.currencyCode])

	const totalPending = useMemo(() => roulette.pendingChips.reduce((sum, chip) => sum + chip.amount, 0), [roulette.pendingChips])
	const selectedChipValue = ROULETTE_CHIP_REAL_VALUES[roulette.selectedChipIndex] ?? 0
	const totalPendingUsd = useMemo(() => {
		const rate = Number(currency?.exchange_rate ?? 0)
		if (!Number.isFinite(totalPending) || !Number.isFinite(rate)) {
			return 0
		}
		return totalPending * rate
	}, [currency?.exchange_rate, totalPending])
	const controlsLocked = !isAuthenticated || roulette.isSubmitting || roulette.isWheelSpinning || roulette.phase !== 'ready'
	const hasPending = roulette.pendingChips.length > 0

	const updateScrollState = useCallback(() => {
		const container = scrollRef.current
		if (!container) {
			return
		}

		setCanScrollPrev(container.scrollLeft > 0)
		setCanScrollNext(container.scrollLeft + container.clientWidth < container.scrollWidth - 1)
	}, [])

	useEffect(() => {
		const container = scrollRef.current
		if (!container) {
			return
		}

		updateScrollState()
		container.addEventListener('scroll', updateScrollState, { passive: true })
		window.addEventListener('resize', updateScrollState)

		return () => {
			container.removeEventListener('scroll', updateScrollState)
			window.removeEventListener('resize', updateScrollState)
		}
	}, [updateScrollState])

	useEffect(() => {
		const button = chipButtonRefs.current[roulette.selectedChipIndex]
		button?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
	}, [roulette.selectedChipIndex])

	const scrollChips = (direction: 'prev' | 'next') => {
		scrollRef.current?.scrollBy({
			left: direction === 'next' ? CHIP_SCROLL_STEP_PX : -CHIP_SCROLL_STEP_PX,
			behavior: 'smooth',
		})
	}

	const primaryAction = useMemo(() => {
		if (!isAuthenticated) {
			return { label: 'Bet', variant: 'secondary' as const, disabled: true, onClick: () => undefined }
		}

		return {
			label: 'Bet',
			variant: 'default' as const,
			disabled: controlsLocked || !hasPending,
			onClick: () => void controller?.startGame(),
		}
	}, [controller, controlsLocked, hasPending, isAuthenticated])

	return (
		<div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4', className)}>
			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
			<div className={cn('space-y-2', controlsLocked && 'pointer-events-none opacity-50')}>
				<div className='flex items-center gap-2'>
					<span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Chip Value</span>
					<span className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>{formatBalance(selectedChipValue, 8)}</span>
					<CurrencyIcon code={roulette.currencyCode} symbolUrl={currency?.symbol_url} size='sm' />
				</div>

				<div className='flex items-center gap-1 rounded-xl border border-border bg-secondary px-1 py-2'>
					<button
						type='button'
						aria-label='Previous chips'
						disabled={controlsLocked || !canScrollPrev}
						onClick={() => scrollChips('prev')}
						className='flex h-10 w-8 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors disabled:pointer-events-none disabled:opacity-35'
					>
						<ChevronLeft className='h-5 w-5' />
					</button>

					<div ref={scrollRef} className='flex min-w-0 flex-1 items-center gap-3 overflow-x-auto scroll-smooth px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
						{ROULETTE_CHIP_REAL_VALUES.map((_, index) => {
							const selected = roulette.selectedChipIndex === index

							return (
								<button
									key={index}
									ref={(node) => {
										chipButtonRefs.current[index] = node
									}}
									type='button'
									disabled={controlsLocked}
									onClick={() => controller?.selectChip(index)}
									className={cn(
										'shrink-0 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
										selected ? 'ring-2 ring-white ring-offset-2 ring-offset-secondary' : 'opacity-90 hover:opacity-100',
									)}
								>
									<Chip chipIndex={index} size={36} />
								</button>
							)
						})}
					</div>

					<button
						type='button'
						aria-label='Next chips'
						disabled={controlsLocked || !canScrollNext}
						onClick={() => scrollChips('next')}
						className='flex h-10 w-8 shrink-0 items-center justify-center rounded-lg text-foreground transition-colors disabled:pointer-events-none disabled:opacity-35'
					>
						<ChevronRight className='h-5 w-5' />
					</button>
				</div>
			</div>

			<div className={cn('space-y-2', (!hasPending || controlsLocked) && 'opacity-80')}>
				<div className='flex items-center justify-between gap-3'>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Total Bet</label>
					<span className='text-xs font-medium text-muted-foreground'>${formatBalance(totalPendingUsd, 2)}</span>
				</div>
				<div className='flex h-11 items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
					<div className='relative flex min-w-0 flex-1 items-center px-3 pr-11'>
						<span className='truncate text-sm font-semibold text-foreground'>{formatBalance(totalPending, 8)}</span>
						<CurrencyIcon
							code={roulette.currencyCode}
							symbolUrl={currency?.symbol_url}
							size='sm'
							className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'
						/>
					</div>
					<div className='flex w-[88px] shrink-0 border-l border-border'>
						<button
							type='button'
							disabled={controlsLocked || !hasPending}
							onClick={() => controller?.scaleBets(0.5)}
							className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50'
						>
							½
						</button>
						<span className='w-px bg-border' aria-hidden />
						<button
							type='button'
							disabled={controlsLocked || !hasPending}
							onClick={() => controller?.scaleBets(2)}
							className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50'
						>
							2×
						</button>
					</div>
				</div>
			</div>

			{(roulette.status === 'win' || roulette.status === 'lost' || roulette.status === 'draw') && roulette.currentMultiplier > 0 ? (
				<div className='rounded-xl border border-border bg-secondary/60 px-4 py-3'>
					<p className='text-xs uppercase tracking-wide text-muted-foreground'>Result</p>
					<p className='mt-1 text-lg font-semibold text-foreground'>
						{roulette.currentMultiplier.toFixed(2)}× · {formatBalance(roulette.currentPayout, 4)}
					</p>
				</div>
			) : null}
			</div>

			<div className='shrink-0'>
				<Button
					type='button'
					variant={primaryAction.variant}
					disabled={primaryAction.disabled || !controller}
					onClick={primaryAction.onClick}
					className='h-12 w-full text-base font-semibold'
				>
					{primaryAction.label}
				</Button>
			</div>
		</div>
	)
}
